import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Activity,
  Server,
  Database,
  Cpu,
  HardDrive,
  MemoryStick,
  Wifi,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Users,
  TrendingUp,
  Shield,
  Zap,
  Globe
} from "lucide-react";

export default function SystemHealth() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadHealthData = useCallback(async () => {
    try {
      const [healthRes, statsRes] = await Promise.all([
        api.get("/system/health/detailed"),
        api.get("/system/stats/overview")
      ]);
      
      setHealthData(healthRes.data);
      setStatsData(statsRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to load health data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !["admin", "administrator"].includes(user?.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    
    loadHealthData();
  }, [user, navigate, authLoading, loadHealthData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(loadHealthData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadHealthData]);

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy": return "text-green-600 bg-green-100";
      case "degraded": return "text-amber-600 bg-amber-100";
      case "unhealthy": return "text-red-600 bg-red-100";
      default: return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "healthy": return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "degraded": return <AlertTriangle className="h-5 w-5 text-amber-600" />;
      case "unhealthy": return <XCircle className="h-5 w-5 text-red-600" />;
      default: return <Activity className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-400 mb-4" />
          <p className="text-slate-400">Loading System Health...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-900/50 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-500/20 rounded-lg">
                <Activity className="h-6 w-6 text-teal-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold">System Health Monitor</h1>
                <p className="text-sm text-slate-400">Real-time system status and performance</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right text-sm">
                <p className="text-slate-400">Last updated</p>
                <p className="text-slate-300">{lastRefresh?.toLocaleTimeString()}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? "Pause" : "Resume"} Auto-refresh
              </Button>
              <Button 
                size="sm"
                className="bg-teal-600 hover:bg-teal-700"
                onClick={loadHealthData}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Overall Status Banner */}
        {healthData && (
          <Card className={`mb-6 border-0 ${
            healthData.status === "healthy" ? "bg-green-900/30" :
            healthData.status === "degraded" ? "bg-amber-900/30" : "bg-red-900/30"
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getStatusIcon(healthData.status)}
                  <div>
                    <h2 className="text-lg font-semibold">
                      System Status: <span className="capitalize">{healthData.status}</span>
                    </h2>
                    <p className="text-sm text-slate-300">
                      Version {healthData.version} • Environment: {healthData.environment}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-300">
                    Uptime: {formatUptime(healthData.metrics.uptime_seconds)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts */}
        {healthData?.alerts?.length > 0 && (
          <div className="mb-6 space-y-2">
            {healthData.alerts.map((alert, idx) => (
              <Alert 
                key={idx} 
                className={`border-0 ${
                  alert.level === "critical" ? "bg-red-900/50 text-red-200" : "bg-amber-900/50 text-amber-200"
                }`}
              >
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{alert.message}</AlertDescription>
              </Alert>
            ))}
          </div>
        )}

        {/* Resource Metrics */}
        {healthData?.metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Cpu className="h-5 w-5 text-blue-400" />
                  <span className="text-2xl font-bold text-blue-400">
                    {healthData.metrics.cpu_percent}%
                  </span>
                </div>
                <p className="text-sm text-slate-400">CPU Usage</p>
                <Progress 
                  value={healthData.metrics.cpu_percent} 
                  className="h-1.5 mt-2 bg-slate-700"
                />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <MemoryStick className="h-5 w-5 text-purple-400" />
                  <span className="text-2xl font-bold text-purple-400">
                    {healthData.metrics.memory_percent}%
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  Memory ({Math.round(healthData.metrics.memory_used_mb)}MB / {Math.round(healthData.metrics.memory_total_mb)}MB)
                </p>
                <Progress 
                  value={healthData.metrics.memory_percent} 
                  className="h-1.5 mt-2 bg-slate-700"
                />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <HardDrive className="h-5 w-5 text-amber-400" />
                  <span className="text-2xl font-bold text-amber-400">
                    {healthData.metrics.disk_percent}%
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  Disk ({healthData.metrics.disk_used_gb}GB / {healthData.metrics.disk_total_gb}GB)
                </p>
                <Progress 
                  value={healthData.metrics.disk_percent} 
                  className="h-1.5 mt-2 bg-slate-700"
                />
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Wifi className="h-5 w-5 text-green-400" />
                  <span className="text-2xl font-bold text-green-400">
                    {healthData.metrics.active_connections}
                  </span>
                </div>
                <p className="text-sm text-slate-400">Active Connections</p>
                <div className="h-1.5 mt-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-400 rounded-full" style={{width: '60%'}}></div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Services Status */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-slate-200 flex items-center gap-2">
                <Server className="h-5 w-5 text-teal-400" />
                Services
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {healthData?.services?.map((service, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {service.name === "MongoDB" ? (
                      <Database className="h-5 w-5 text-green-400" />
                    ) : (
                      <Globe className="h-5 w-5 text-blue-400" />
                    )}
                    <div>
                      <p className="font-medium text-slate-200">{service.name}</p>
                      {service.latency_ms && (
                        <p className="text-xs text-slate-400">{service.latency_ms}ms latency</p>
                      )}
                    </div>
                  </div>
                  <Badge className={`${getStatusColor(service.status)} border-0`}>
                    {service.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Application Stats */}
          {statsData && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-slate-200 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-teal-400" />
                  Application Stats
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                    <Users className="h-5 w-5 mx-auto text-blue-400 mb-1" />
                    <p className="text-2xl font-bold text-slate-200">{statsData.users.total}</p>
                    <p className="text-xs text-slate-400">Total Users</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                    <Shield className="h-5 w-5 mx-auto text-purple-400 mb-1" />
                    <p className="text-2xl font-bold text-slate-200">{statsData.users.staff}</p>
                    <p className="text-xs text-slate-400">Staff</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                    <Activity className="h-5 w-5 mx-auto text-green-400 mb-1" />
                    <p className="text-2xl font-bold text-slate-200">{statsData.activity.checkins_today}</p>
                    <p className="text-xs text-slate-400">Checkins Today</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                    <Zap className="h-5 w-5 mx-auto text-amber-400 mb-1" />
                    <p className="text-2xl font-bold text-slate-200">{statsData.activity.active_sessions}</p>
                    <p className="text-xs text-slate-400">Active Sessions</p>
                  </div>
                </div>
                
                <div className="p-3 bg-slate-900/50 rounded-lg">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Patients/Caregivers</span>
                    <span className="text-slate-200">{statsData.users.patients}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Partners</span>
                    <span className="text-slate-200">{statsData.users.partners}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Errors */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-slate-200 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                Recent Errors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {healthData?.recent_errors?.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {healthData.recent_errors.map((error, idx) => (
                    <div 
                      key={idx}
                      className="p-2 bg-red-900/20 border border-red-900/50 rounded text-sm"
                    >
                      <p className="text-red-300 font-medium">{error.action}</p>
                      <p className="text-red-400/70 text-xs">
                        {error.timestamp ? new Date(error.timestamp).toLocaleString() : 'Unknown time'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-400 mb-2" />
                  <p className="text-slate-400">No recent errors</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Mobile Install Banner */}
        <Card className="mt-6 bg-gradient-to-r from-teal-900/50 to-cyan-900/50 border-teal-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-500/20 rounded-lg">
                  <Globe className="h-6 w-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-teal-200">Mobile App Ready</h3>
                  <p className="text-sm text-teal-300/70">
                    Goodwin Cares can be installed as a mobile app on any device
                  </p>
                </div>
              </div>
              <Badge className="bg-teal-600 text-white border-0">PWA Enabled</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
